<template>
  <b-container class="page-stack-index px-3" fluid>
    <base-loading-state v-if="isLoading" />

    <div v-else>
      <div class="stack-header">
        <h4 class="title__stack-header">Stack Managing</h4>
        <b-button
          variant="first"
          class="button__create-stack"
          @click="$router.push({ name: 'create-stack' })"
        >
          <span> Create New Stack </span>
        </b-button>
      </div>

      <div v-if="stack && stack.length">
        <div class="container__table-stack">
          <TableStack :stack="stack" @edit="onEdit" />
        </div>
        <div class="pagination-container d-flex">
          <div>
            <p class="m-0 text-black-50">
              Showing
              {{ (currentPage - 1) * perPage + 1 }}
              -
              {{ Math.min(currentPage * perPage, totalItems) }}
              out of
              {{ totalItems }}
            </p>
          </div>

          <div class="d-flex align-items-center">
            <b-pagination
              ref="pagination"
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="perPage"
              first-number
              last-number
              align="center"
              class="dropdown-menu-pagination"
            />
          </div>

          <div class="d-flex align-items-center">
            <div class="text-black-50">Items per page:</div>
            <b-dropdown
              v-model="perPage"
              :text="perPage.toString()"
              class="per-page-dropdown ml-3"
              size="sm"
              menu-class="dropdown-menu-per-page"
              variant="outline-dark bg-transparent text-dark"
            >
              <b-dropdown-item
                v-for="option in perPageOptions"
                :key="option"
                :value="option"
                class="dropdown-item"
                @click="perPage = option"
              >
                {{ option }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <b-img class="img__empty-state" :src="emptyStatePng" alt="Empty State"></b-img>
        <div class="text__empty-state">
          <p class="p-0 m-0">There are no stacks currently.</p>
          <p class="p-0 m-0">Start creating new stacks and manage them.</p>
        </div>
      </div>
    </div>
  </b-container>
</template>

<script>
import emptyStatePng from '@/assets/stack/empty-state.png';
import TableStack from '@/views/Stack/components/TableStack.vue';
import Stack from '@/models/Stack';
import BaseLoadingState from '@/components/base-components/BaseLoadingState';

export default {
  name: 'PageStackIndex',
  components: {
    TableStack,
    BaseLoadingState
  },

  data() {
    return {
      isLoading: false,
      emptyStatePng,
      sortedStack: [],
      stack: null,
      perPage: 10,
      perPageOptions: [10, 20, 30, 40, 50],
      currentPage: 1,
      totalItems: 0
    };
  },
  watch: {
    currentPage: {
      handler(newPage) {
        this.fetchStacks(newPage);
      }
    },
    perPage: {
      handler() {
        this.currentPage = 1;
        this.fetchStacks(1);
      }
    }
  },
  mounted() {
    this.fetchStacks(this.currentPage);
  },
  methods: {
    async fetchStacks(page = 1) {
      this.isLoading = true;

      try {
        const {
          response: { data }
        } = await Stack.api().fetchWithPagination(page, this.perPage);

        this.stack = data.items;
        this.totalItems = data.pagination.totalItems;
      } catch (error) {
        this.$toast.error('Cannot load stack, please try again');
      } finally {
        this.isLoading = false;
      }
    },
    onEdit(item) {
      this.$router.push({
        name: 'edit-stack',
        params: { id: item.id }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.page-stack-index {
  max-width: 960px;
}

.stack-header {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  align-items: start;
  margin-bottom: 32px;

  .title__stack-header {
    font-weight: 700;
  }
  .button__create-stack {
    justify-self: end;
    width: max-content;
    font-size: 16px;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 38px;
  padding: 32px 0;

  .text__empty-state {
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: column;
  }
}

.per-page-dropdown {
  width: 70px;

  ::v-deep .btn {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  ::v-deep .dropdown-menu {
    min-width: 70px;
    padding: 0;
    display: none;
  }

  ::v-deep .dropdown-menu.show {
    display: block;
  }

  ::v-deep .dropdown-item {
    text-align: center;
    padding: 0.25rem 0.5rem;
  }
}

.dropdown-menu-pagination {
  margin: 16px 0;
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
}

@media screen and (max-width: 576px) {
  .pagination-container {
    flex-direction: column;
    margin-bottom: 20px;
  }
}

.container__table-stack {
  display: grid;
  grid-template-columns: 1fr;
  overflow-x: auto;
  width: 100%;
}
</style>
